/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import {
  Container,
  Grid,
  Typography,
  TextField,
  InputAdornment,
  Button,
  Box,
  Badge,
  IconButton,
  Skeleton,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import RefreshIcon from '@mui/icons-material/Refresh';
import CloseIcon from '@mui/icons-material/Close';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import PhoneIcon from '@mui/icons-material/Phone';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import GavelIcon from '@mui/icons-material/Gavel';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import BlockIcon from '@mui/icons-material/Block';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import BookOnlineIcon from '@mui/icons-material/BookOnline';
import FilterListIcon from '@mui/icons-material/FilterList';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import EmailIcon from '@mui/icons-material/Email';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, getDocFromServer } from 'firebase/firestore';
import { db } from '../../firebase';

import PageBanner from '../../components/PageBanner';
import SEO from '../../components/SEO';
import SectionSidebar from '../../components/SectionSidebar';
import PoojaCard from '../../components/PoojaCard';
import PoojaBookingModal from '../../components/PoojaBookingModal';
import { poojas as defaultPoojas, Pooja } from '../../data/pooja-data';
import { useCart } from '../../context/CartContext';

const GOLD = '#d4af37';

const deityTabs = [
  { key: 'All', label: 'All' },
  { key: 'BHAGAVATHY', label: 'Devi' },
  { key: 'GANAPATHY', label: 'Ganapathy' },
  { key: 'VISHNU', label: 'Vishnu' },
  { key: 'SIVA', label: 'Shiva' },
  { key: 'AYYAPPA', label: 'Ayyappa' },
  { key: 'GENERAL', label: 'General' },
];

interface VazhipadPrice {
  id: string;
  name: string;
  price: string;
  description: string;
}

interface BookingSettings {
  enabled: boolean;
  title: string;
  subtitle: string;
  description: string;
  instructions: string;
  contactEmail: string;
  contactPhone: string;
  minAdvanceDays: number;
  maxAdvanceDays: number;
  termsAndConditions: string;
  vazhipadPrices: VazhipadPrice[];
}

export default function Booking() {
  const [settings, setSettings] = useState<BookingSettings>({
    enabled: true,
    title: 'Online Pooja Booking',
    subtitle: 'Book Your Pooja',
    description: 'Select poojas, add booking details, and complete your reservation',
    instructions: '',
    contactEmail: '',
    contactPhone: '',
    minAdvanceDays: 2,
    maxAdvanceDays: 90,
    termsAndConditions: '',
    vazhipadPrices: [],
  });
  const [allPoojas, setAllPoojas] = useState<Pooja[]>(defaultPoojas);
  const [loadingPoojas, setLoadingPoojas] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedDeity, setSelectedDeity] = useState('All');
  const [showInfo, setShowInfo] = useState(false);
  const { cart } = useCart();
  const navigate = useNavigate();

  const [modalOpen, setModalOpen] = useState(false);
  const [selectedPooja, setSelectedPooja] = useState<Pooja[] | null>(null);
  const [selectedPoojaIds, setSelectedPoojaIds] = useState<number[]>([]);

  useEffect(() => {
    fetchBookingSettings();
    loadPoojas();
  }, []);

  const loadPoojas = async () => {
    setLoadingPoojas(true);
    try {
      const docRef = doc(db, 'pooja_data', 'main');
      const docSnap = await getDocFromServer(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        if (data.poojas && Array.isArray(data.poojas) && data.poojas.length > 0) {
          setAllPoojas(data.poojas);
          setLastRefreshTime(new Date().toLocaleString());
        } else {
          setAllPoojas(defaultPoojas);
        }
      } else {
        setAllPoojas(defaultPoojas);
      }
    } catch (error) {
      setAllPoojas(defaultPoojas);
    } finally {
      setLoadingPoojas(false);
    }
  };

  const fetchBookingSettings = async () => {
    try {
      const docRef = doc(db, 'pooja_booking_settings', 'main');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const data = docSnap.data() as BookingSettings;
        setSettings({
          enabled: data.enabled !== false,
          title: data.title || 'Online Pooja Booking',
          subtitle: data.subtitle || 'Book Your Pooja',
          description: data.description || 'Select poojas, add booking details, and complete your reservation',
          instructions: data.instructions || '',
          contactEmail: data.contactEmail || '',
          contactPhone: data.contactPhone || '',
          minAdvanceDays: data.minAdvanceDays || 2,
          maxAdvanceDays: data.maxAdvanceDays || 90,
          termsAndConditions: data.termsAndConditions || '',
          vazhipadPrices: data.vazhipadPrices || [],
        });
      }
    } catch (error) {
      console.error('Error fetching booking settings:', error);
    }
  };

  const handlePoojaSelect = useCallback((poojaId: number) => {
    setSelectedPoojaIds(prev =>
      prev.includes(poojaId)
        ? prev.filter(id => id !== poojaId)
        : [...prev, poojaId]
    );
  }, []);

  const handleOpenModal = () => {
    if (selectedPoojaIds.length > 0) {
      const selectedPoojasData = allPoojas.filter(p => selectedPoojaIds.includes(p.id));
      if (selectedPoojasData.length > 0) {
        setSelectedPooja(selectedPoojasData);
        setModalOpen(true);
      }
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedPooja(null);
    setSelectedPoojaIds([]);
  };

  const handleCancelModal = () => {
    setModalOpen(false);
    setSelectedPooja(null);
    setSelectedPoojaIds([]);
  };

  const filteredPoojas = useMemo(() => {
    return allPoojas.filter(p => {
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesDeity = selectedDeity === 'All' || p.deity === selectedDeity;
      return matchesSearch && matchesDeity;
    });
  }, [searchTerm, selectedDeity, allPoojas]);

  const totalItems = cart.reduce((sum, item) => sum + item.quantity, 0);
  const hasInfo = settings.instructions || settings.contactEmail || settings.contactPhone || settings.termsAndConditions;

  return (
    <Box sx={{ bgcolor: '#fafaf8', minHeight: '100vh' }}>
      <SEO title="Pooja Booking Online — Kottayil Temple | പൂജ ബുക്കിംഗ്" description="Book poojas and vazhipads online at Kottayil Temple (Kotta-Kovilakam Kshethram). Secure online payment, instant confirmation. Book Archana, Abhishekam, Homam and more." path="/poojas/booking" keywords="pooja booking, online pooja booking, Kottayil temple booking, vazhipad booking, book pooja online, പൂജ ബുക്കിംഗ്, ഓൺലൈൻ വഴിപാട്, കൊട്ടയിൽ ക്ഷേത്രം ബുക്കിംഗ്, online vazhipadu" />
      <PageBanner title="Pooja Booking" />
      <Container maxWidth="lg" sx={{ mt: { xs: -4, md: -6 }, position: 'relative', zIndex: 2, pb: totalItems > 0 ? 12 : 10 }}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={8} lg={9}>

            {/* ══════ VIBRANT HERO ══════ */}
            <Box sx={{
              borderRadius: '24px', overflow: 'hidden', mb: 3.5,
              background: 'linear-gradient(135deg, #134e4a 0%, #0d9488 50%, #14b8a6 100%)',
              position: 'relative',
            }}>
              <Box sx={{ position: 'absolute', inset: 0, opacity: 0.07, backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)', backgroundSize: '20px 20px' }} />
              <Box sx={{ position: 'absolute', top: -60, right: -60, width: 200, height: 200, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%)' }} />
              <Box sx={{ position: 'absolute', bottom: -40, left: -40, width: 160, height: 160, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.08) 0%, transparent 70%)' }} />

              <Box sx={{ px: { xs: 3, sm: 4 }, pt: { xs: 3.5, sm: 4 }, pb: { xs: 4, sm: 5 }, position: 'relative', zIndex: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2.5 }}>
                  <Box sx={{
                    width: 64, height: 64, borderRadius: '20px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <BookOnlineIcon sx={{ fontSize: 32, color: '#fff' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Chip label="Sacred Offerings" size="small" icon={<LocalFireDepartmentIcon sx={{ fontSize: 14, color: '#fff !important' }} />} sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.7rem', mb: 1, height: 24, backdropFilter: 'blur(8px)' }} />
                    <Typography sx={{ fontWeight: 900, fontSize: { xs: '1.5rem', sm: '1.8rem' }, color: '#fff', lineHeight: 1.2 }}>
                      {settings.subtitle}
                    </Typography>
                  </Box>
                </Box>
                <Typography sx={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.8)', lineHeight: 1.8, maxWidth: 600 }}>
                  {settings.description}
                </Typography>

                {/* Action buttons */}
                <Box sx={{ display: 'flex', gap: 1.5, mt: 2.5, flexWrap: 'wrap' }}>
                  {hasInfo && (
                    <Button
                      onClick={() => setShowInfo(!showInfo)}
                      startIcon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                      size="small"
                      sx={{
                        borderRadius: '12px', px: 2, py: 0.7,
                        bgcolor: showInfo ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)',
                        color: '#fff', fontWeight: 700, fontSize: '0.75rem',
                        textTransform: 'none', backdropFilter: 'blur(8px)',
                        border: '1px solid rgba(255,255,255,0.2)',
                        '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                      }}
                    >
                      {showInfo ? 'Hide Info' : 'Booking Info'}
                    </Button>
                  )}
                  <Button
                    onClick={loadPoojas}
                    disabled={loadingPoojas}
                    startIcon={<RefreshIcon sx={{ fontSize: 16, animation: loadingPoojas ? 'spin 1s linear infinite' : 'none', '@keyframes spin': { to: { transform: 'rotate(360deg)' } } }} />}
                    size="small"
                    sx={{
                      borderRadius: '12px', px: 2, py: 0.7,
                      bgcolor: 'rgba(255,255,255,0.12)',
                      color: '#fff', fontWeight: 700, fontSize: '0.75rem',
                      textTransform: 'none', backdropFilter: 'blur(8px)',
                      border: '1px solid rgba(255,255,255,0.2)',
                      '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
                    }}
                  >
                    {loadingPoojas ? 'Loading...' : 'Refresh'}
                  </Button>
                </Box>
              </Box>
            </Box>

            {/* ══════ BOOKING DISABLED WARNING ══════ */}
            {!settings.enabled && (
              <Box sx={{
                mb: 3, borderRadius: '20px', overflow: 'hidden',
                border: '1.5px solid rgba(245,158,11,0.3)',
                bgcolor: '#fffbeb',
                boxShadow: '0 4px 20px rgba(245,158,11,0.08)',
              }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                  px: { xs: 2.5, sm: 3 }, py: 1.5,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <BlockIcon sx={{ fontSize: 20, color: '#fff' }} />
                  <Typography sx={{ fontWeight: 800, fontSize: '0.9rem', color: '#fff' }}>
                    Online Pooja Booking Disabled
                  </Typography>
                </Box>
                <Box sx={{ px: { xs: 2.5, sm: 3 }, py: 2 }}>
                  <Typography sx={{ fontSize: '0.85rem', color: '#92400e', lineHeight: 1.7, fontWeight: 600 }}>
                    Online pooja booking is currently not available. Please contact the temple office directly for booking poojas.
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                    <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} />
                    <Typography sx={{ fontSize: '0.78rem', color: '#b45309', fontWeight: 500 }}>
                      Visit the Contact page or call the temple office for assistance.
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* ══════ INFO PANEL (collapsible) ══════ */}
            {showInfo && hasInfo && (
              <Box sx={{
                bgcolor: '#fff', borderRadius: '24px',
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                overflow: 'hidden', mb: 3.5,
              }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                  px: { xs: 2.5, sm: 3 }, py: 2,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 40, height: 40, borderRadius: '12px',
                      bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.25)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <InfoOutlinedIcon sx={{ fontSize: 20, color: '#fff' }} />
                    </Box>
                    <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                      Booking Information
                    </Typography>
                  </Box>
                  <IconButton onClick={() => setShowInfo(false)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff' } }}>
                    <CloseIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                </Box>

                <Box sx={{ px: { xs: 2.5, sm: 3.5 }, py: 2.5 }}>
                  <Grid container spacing={2.5}>
                    {settings.instructions && (
                      <Grid item xs={12} md={6}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '10px',
                            background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <InfoOutlinedIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0d9488', letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
                              Instructions
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.7 }}>
                              {settings.instructions}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {(settings.contactEmail || settings.contactPhone) && (
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '10px',
                            background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <PhoneIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0d9488', letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
                              Contact
                            </Typography>
                            {settings.contactPhone && (
                              <Typography sx={{ fontSize: '0.82rem', color: '#64748b', mb: 0.3 }}>{settings.contactPhone}</Typography>
                            )}
                            {settings.contactEmail && (
                              <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>{settings.contactEmail}</Typography>
                            )}
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {settings.minAdvanceDays > 0 && settings.maxAdvanceDays > 0 && (
                      <Grid item xs={12} md={3}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '10px',
                            background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <CalendarMonthIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0d9488', letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
                              Booking Window
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: '#64748b' }}>
                              {settings.minAdvanceDays} to {settings.maxAdvanceDays} days in advance
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                    {settings.termsAndConditions && (
                      <Grid item xs={12}>
                        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start' }}>
                          <Box sx={{
                            width: 36, height: 36, borderRadius: '10px',
                            background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            flexShrink: 0,
                          }}>
                            <GavelIcon sx={{ fontSize: 16, color: '#fff' }} />
                          </Box>
                          <Box>
                            <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#0d9488', letterSpacing: 1, textTransform: 'uppercase', mb: 0.5 }}>
                              Terms & Conditions
                            </Typography>
                            <Typography sx={{ fontSize: '0.82rem', color: '#64748b', lineHeight: 1.7 }}>
                              {settings.termsAndConditions}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    )}
                  </Grid>
                </Box>
              </Box>
            )}

            {/* ══════ VAZHIPAD PRICES CARD ══════ */}
            {settings.vazhipadPrices.length > 0 && (
              <Box sx={{
                bgcolor: '#fff', borderRadius: '24px',
                border: '1px solid rgba(0,0,0,0.06)',
                boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
                overflow: 'hidden', mb: 3.5,
              }}>
                <Box sx={{
                  background: 'linear-gradient(135deg, #d97706, #f59e0b)',
                  px: { xs: 2.5, sm: 3 }, py: 2,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: '12px',
                    bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255,255,255,0.25)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <LocalOfferIcon sx={{ fontSize: 20, color: '#fff' }} />
                  </Box>
                  <Typography sx={{ fontWeight: 800, fontSize: { xs: '0.88rem', md: '1rem' }, color: '#fff' }}>
                    Vazhipad Prices
                  </Typography>
                </Box>

                <Box sx={{ px: { xs: 2, sm: 3 }, py: 2.5, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                  {settings.vazhipadPrices.map((v) => (
                    <Box key={v.id} sx={{
                      px: 2.5, py: 1.8, borderRadius: '16px',
                      bgcolor: '#f8fafc',
                      border: '1px solid rgba(0,0,0,0.06)',
                      transition: 'all 250ms ease',
                      '&:hover': { borderColor: 'rgba(13,148,136,0.3)', boxShadow: '0 4px 16px rgba(13,148,136,0.08)', transform: 'translateY(-2px)' },
                    }}>
                      <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: '#1e293b', mb: 0.3 }}>
                        {v.name}
                      </Typography>
                      <Typography sx={{ fontSize: '0.95rem', fontWeight: 800, color: '#0d9488' }}>
                        {v.price}
                      </Typography>
                      {v.description && (
                        <Typography sx={{ fontSize: '0.72rem', color: '#94a3b8', mt: 0.3 }}>{v.description}</Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              </Box>
            )}

            {/* ══════ FILTER & SEARCH CARD ══════ */}
            <Box sx={{
              bgcolor: '#fff', borderRadius: '24px',
              border: '1px solid rgba(0,0,0,0.06)',
              boxShadow: '0 4px 24px rgba(0,0,0,0.04)',
              overflow: 'hidden', mb: 3.5,
              position: 'sticky', top: 0, zIndex: 50,
            }}>
              <Box sx={{
                background: 'linear-gradient(135deg, #134e4a, #0d9488)',
                px: { xs: 2.5, sm: 3 }, py: 1.8,
                display: 'flex', alignItems: 'center', gap: 1.5,
              }}>
                <Box sx={{
                  width: 36, height: 36, borderRadius: '10px',
                  bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <FilterListIcon sx={{ fontSize: 18, color: '#fff' }} />
                </Box>
                <Typography sx={{ fontWeight: 800, fontSize: '0.92rem', color: '#fff', flex: 1 }}>
                  Search & Filter
                </Typography>
                <Chip
                  label={`${filteredPoojas.length} result${filteredPoojas.length !== 1 ? 's' : ''}`}
                  size="small"
                  sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: '#fff', fontWeight: 700, fontSize: '0.68rem', height: 24, backdropFilter: 'blur(8px)' }}
                />
              </Box>

              <Box sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                {/* Search */}
                <TextField
                  placeholder="Search poojas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  size="small"
                  fullWidth
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon sx={{ fontSize: 18, color: '#94a3b8' }} />
                      </InputAdornment>
                    ),
                    endAdornment: searchTerm ? (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setSearchTerm('')}
                          sx={{ color: '#94a3b8', '&:hover': { color: '#0d9488' } }}>
                          <CloseIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ) : null,
                  }}
                  sx={{
                    mb: 1.5,
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '14px', bgcolor: '#f8fafc', fontSize: '0.85rem',
                      '& fieldset': { borderColor: 'rgba(0,0,0,0.08)' },
                      '&:hover fieldset': { borderColor: 'rgba(13,148,136,0.3)' },
                      '&.Mui-focused fieldset': { borderColor: '#0d9488', borderWidth: 1.5 },
                    },
                  }}
                />

                {/* Deity filter tabs */}
                <Box sx={{
                  display: 'flex', gap: 0.8, flexWrap: 'wrap',
                }}>
                  {deityTabs.map((tab) => {
                    const isActive = selectedDeity === tab.key;
                    const count = tab.key === 'All'
                      ? allPoojas.length
                      : allPoojas.filter(p => p.deity === tab.key).length;
                    return (
                      <Chip
                        key={tab.key}
                        label={`${tab.label} (${count})`}
                        size="small"
                        onClick={() => setSelectedDeity(tab.key)}
                        sx={{
                          fontWeight: isActive ? 700 : 500,
                          fontSize: '0.75rem',
                          height: 30,
                          color: isActive ? '#fff' : '#64748b',
                          bgcolor: isActive ? '#0d9488' : 'rgba(0,0,0,0.04)',
                          border: isActive ? '1px solid #0d9488' : '1px solid rgba(0,0,0,0.06)',
                          cursor: 'pointer',
                          transition: 'all 200ms ease',
                          '&:hover': {
                            bgcolor: isActive ? '#0d9488' : 'rgba(13,148,136,0.08)',
                            color: isActive ? '#fff' : '#0d9488',
                          },
                        }}
                      />
                    );
                  })}
                </Box>
              </Box>
            </Box>

            {/* ══════ SELECTION BAR ══════ */}
            <Box className="notranslate" translate="no" sx={{
              mb: selectedPoojaIds.length > 0 ? 3 : 0,
              borderRadius: '20px', overflow: 'hidden',
              border: selectedPoojaIds.length > 0 ? '1.5px solid rgba(13,148,136,0.2)' : 'none',
              bgcolor: selectedPoojaIds.length > 0 ? 'rgba(13,148,136,0.04)' : 'transparent',
              boxShadow: selectedPoojaIds.length > 0 ? '0 4px 16px rgba(13,148,136,0.06)' : 'none',
              maxHeight: selectedPoojaIds.length > 0 ? 200 : 0,
              transition: 'max-height 300ms ease, margin 300ms ease, opacity 300ms ease',
              opacity: selectedPoojaIds.length > 0 ? 1 : 0,
            }}>
                <Box sx={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  px: { xs: 2, sm: 3 }, py: 1.5, flexWrap: 'wrap', gap: 1,
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Box sx={{
                      width: 30, height: 30, borderRadius: '50%',
                      background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                      color: '#fff',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '0.78rem', fontWeight: 800,
                      boxShadow: '0 2px 8px rgba(13,148,136,0.35)',
                    }}>
                      {selectedPoojaIds.length}
                    </Box>
                    <Typography sx={{ fontSize: '0.85rem', color: '#0d9488', fontWeight: 600 }}>
                      pooja{selectedPoojaIds.length > 1 ? 's' : ''} selected
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      size="small"
                      onClick={() => setSelectedPoojaIds([])}
                      sx={{
                        color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600,
                        textTransform: 'none', px: 2, borderRadius: '10px',
                        '&:hover': { color: '#0d9488', bgcolor: 'rgba(13,148,136,0.06)' },
                      }}
                    >
                      Clear
                    </Button>
                    {settings.enabled && (
                      <Button
                        size="small"
                        onClick={handleOpenModal}
                        endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                        sx={{
                          background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                          color: '#fff', fontSize: '0.78rem', fontWeight: 700,
                          textTransform: 'none', px: 3, borderRadius: '12px',
                          boxShadow: '0 4px 16px rgba(13,148,136,0.35)',
                          '&:hover': { boxShadow: '0 6px 20px rgba(13,148,136,0.45)' },
                        }}
                      >
                        Book Selected
                      </Button>
                    )}
                  </Box>
                </Box>
            </Box>

            {/* ══════ POOJA GRID ══════ */}
            {loadingPoojas ? (
              <Grid container spacing={3}>
                {Array.from({ length: 8 }).map((_, i) => (
                  <Grid item xs={6} sm={4} key={i}>
                    <Skeleton variant="rounded" height={200} sx={{
                      bgcolor: 'rgba(13,148,136,0.06)', borderRadius: '20px',
                    }} />
                  </Grid>
                ))}
              </Grid>
            ) : filteredPoojas.length > 0 ? (
              <Grid container spacing={3}>
                {filteredPoojas.map(pooja => (
                  <Grid item xs={6} sm={4} key={pooja.id}>
                    <PoojaCard
                      pooja={pooja}
                      onSelect={handlePoojaSelect}
                      isSelected={selectedPoojaIds.includes(pooja.id)}
                    />
                  </Grid>
                ))}
              </Grid>
            ) : (
              <Box sx={{
                textAlign: 'center', py: 8, px: 3,
                bgcolor: '#fff', borderRadius: '24px',
                border: '1px solid rgba(0,0,0,0.06)',
              }}>
                <SearchIcon sx={{ fontSize: 48, color: '#e2e8f0', mb: 2 }} />
                <Typography sx={{ fontSize: { xs: '0.92rem', md: '1.1rem' }, color: '#94a3b8', fontWeight: 600, mb: 0.5 }}>
                  No poojas found
                </Typography>
                <Typography sx={{ fontSize: '0.82rem', color: '#cbd5e1' }}>
                  Try adjusting your search or filter
                </Typography>
              </Box>
            )}

          </Grid>

          {/* ── Sidebar ── */}
          <Grid item xs={12} md={4} lg={3}>
            <SectionSidebar sectionKey="poojas" activePath="/poojas/booking" />
          </Grid>
        </Grid>
      </Container>

      {/* ══════ BOTTOM SELECTION BAR (FIXED) ══════ */}
      <Box className="notranslate" translate="no" sx={{
          position: 'fixed', bottom: totalItems > 0 ? 56 : 0, left: 0, right: 0,
          zIndex: 99,
          bgcolor: 'rgba(240,253,250,0.97)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(13,148,136,0.15)',
          boxShadow: '0 -4px 24px rgba(13,148,136,0.08)',
          transform: selectedPoojaIds.length > 0 ? 'translateY(0)' : 'translateY(100%)',
          transition: 'transform 300ms ease',
          pointerEvents: selectedPoojaIds.length > 0 ? 'auto' : 'none',
        }}>
          <Container maxWidth="lg">
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              py: 1.2, flexWrap: 'wrap', gap: 1,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box sx={{
                  width: 28, height: 28, borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: '0.78rem', fontWeight: 800,
                  boxShadow: '0 2px 8px rgba(13,148,136,0.35)',
                }}>
                  {selectedPoojaIds.length}
                </Box>
                <Typography sx={{ fontSize: '0.82rem', color: '#0d9488', fontWeight: 600 }}>
                  pooja{selectedPoojaIds.length > 1 ? 's' : ''} selected
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  onClick={() => setSelectedPoojaIds([])}
                  sx={{
                    color: '#94a3b8', fontSize: '0.78rem', fontWeight: 600,
                    textTransform: 'none', px: 2,
                    '&:hover': { color: '#0d9488', bgcolor: 'rgba(13,148,136,0.06)' },
                  }}
                >
                  Clear
                </Button>
                {settings.enabled && (
                  <Button
                    size="small"
                    onClick={handleOpenModal}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: '14px !important' }} />}
                    sx={{
                      background: 'linear-gradient(135deg, #0d9488, #14b8a6)',
                      color: '#fff', fontSize: '0.78rem', fontWeight: 700,
                      textTransform: 'none', px: 3, borderRadius: '12px',
                      boxShadow: '0 4px 16px rgba(13,148,136,0.35)',
                      '&:hover': { boxShadow: '0 6px 20px rgba(13,148,136,0.45)' },
                    }}
                  >
                    Book Selected
                  </Button>
                )}
              </Box>
            </Box>
          </Container>
        </Box>

      {/* ══════ FLOATING CART BAR ══════ */}
      {totalItems > 0 && (
        <Box sx={{
          position: 'fixed', bottom: 0, left: 0, right: 0,
          zIndex: 100,
          bgcolor: 'rgba(255,255,255,0.95)',
          backdropFilter: 'blur(20px)',
          borderTop: '1px solid rgba(13,148,136,0.15)',
          boxShadow: '0 -4px 24px rgba(0,0,0,0.06)',
        }}>
          <Container maxWidth="lg">
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.5,
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Badge badgeContent={totalItems} sx={{
                  '& .MuiBadge-badge': { bgcolor: '#0d9488', color: '#fff', fontWeight: 800, fontSize: '0.7rem' }
                }}>
                  <ShoppingCartIcon sx={{ fontSize: 22, color: '#64748b' }} />
                </Badge>
                <Typography sx={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 500 }}>
                  {totalItems} item{totalItems > 1 ? 's' : ''} in cart
                </Typography>
              </Box>
              <Button
                onClick={() => navigate('/cart')}
                endIcon={<ArrowForwardIcon sx={{ fontSize: '16px !important' }} />}
                sx={{
                  background: `linear-gradient(135deg, ${GOLD}, #f59e0b)`,
                  color: '#fff',
                  fontWeight: 700, fontSize: '0.82rem',
                  px: 4, py: 1, borderRadius: '14px',
                  textTransform: 'none',
                  boxShadow: '0 4px 16px rgba(212,175,55,0.35)',
                  '&:hover': { boxShadow: '0 6px 20px rgba(212,175,55,0.45)' },
                }}
              >
                View Cart
              </Button>
            </Box>
          </Container>
        </Box>
      )}

      <PoojaBookingModal
        open={modalOpen}
        handleClose={handleCloseModal}
        handleCancel={handleCancelModal}
        pooja={null}
        selectedPoojas={selectedPooja || []}
      />
    </Box>
  );
}
